import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { VirtualAppointmentHandoffDto } from '../../../../services/virtualAppointments';
import { VaHandoffMessage } from './VaHandoffMessage';

function handoffKey({ kind, at }: VirtualAppointmentHandoffDto): string {
    return `${at}-${kind}`;
}

interface VaHandoffThreadProps {
    /** Oldest first. */
    handoffs: readonly VirtualAppointmentHandoffDto[];
}

// This round's escalations and hand backs as a Q&A, pinned above the open VAs they concern.
export function VaHandoffThread({ handoffs }: VaHandoffThreadProps) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const latest = handoffs[handoffs.length - 1];
    const latestKey = latest ? handoffKey(latest) : null;

    // Land on the newest message whenever one arrives; the older ones are a scroll up.
    useLayoutEffect(() => {
        const element = scrollRef.current;
        if (element && latestKey) element.scrollTop = element.scrollHeight;
    }, [latestKey, open]);

    // At most about a third of the tab, so the open VAs below always keep room to scroll.
    return (
        <section
            aria-label="Handoff thread"
            className="flex-shrink-0 flex flex-col min-h-0 max-h-[35%] border-b border-gray-200 bg-gray-50"
        >
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                className="flex w-full min-w-0 flex-shrink-0 items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
                <ChevronRightIcon
                    className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                />
                <span className="min-w-0 truncate">Escalations and hand backs</span>
                <span className="ml-1 flex-shrink-0 text-gray-400 font-medium">
                    {handoffs.length}
                </span>
            </button>
            {/* A flex column, so the scroll region shrinks with the section's cap without needing
                a % height; max-h-56 still bounds it on tall panels. */}
            {open && (
                <OverlayScrollArea
                    className="flex-1 min-h-0 flex flex-col"
                    contentClassName="min-h-0 px-3 pb-2.5 max-h-56"
                    trackInsetPx={4}
                    scrollRef={scrollRef}
                >
                    <ol className="flex flex-col gap-2">
                        {handoffs.map((handoff) => (
                            <VaHandoffMessage
                                key={handoffKey(handoff)}
                                handoff={handoff}
                                latest={handoff === latest}
                            />
                        ))}
                    </ol>
                </OverlayScrollArea>
            )}
        </section>
    );
}
